import type { ClientPreferencesComponentInput } from "@repo/shared/types";
import { toast } from "sonner";
import {
  useClientPreferencesFlowInstanceValue,
  useSaveClientPreferences,
} from "@/lib/hooks/use-flow-instances/use-client-preferences";
import { useT } from "@/lib/i18n/use-t";
import { type ClientPreferenceItem, ClientPreferencesWidget } from "../client-preferences-widget";
import { T } from "./wizard-client-preferences.i18n";

type WizardClientPreferencesProps = {
  instanceId: string;
  disabled: boolean;
  config: { style?: "none" | "border" | "box"; title?: string; description?: string };
  /** From the flow definition's portfolio collection (empty = feature unused). */
  preferences: ClientPreferenceItem[];
  language: string;
  hasValidationError?: boolean;
  /** QL-188 — group answering note; null for non-group clients. */
  groupNote?: string | null;
};

export function WizardClientPreferences({
  instanceId,
  disabled,
  config,
  preferences,
  language,
  hasValidationError,
  groupNote,
}: WizardClientPreferencesProps) {
  const { inputData, isLoading: valueLoading } = useClientPreferencesFlowInstanceValue(instanceId);
  const saveClientPreferences = useSaveClientPreferences(instanceId);

  const t = useT(T);

  const handleChange = (input: ClientPreferencesComponentInput) => {
    saveClientPreferences.mutate(input, { onError: () => toast.error(t.saveFailed) });
  };

  if (valueLoading) return null;

  return (
    <ClientPreferencesWidget
      preferences={preferences}
      inputData={inputData}
      onChange={handleChange}
      disabled={disabled}
      style={config.style}
      title={config.title}
      description={config.description}
      language={language}
      hasValidationError={hasValidationError}
      groupNote={groupNote}
    />
  );
}
